import React, { useState, useEffect, useCallback } from 'react';
import { supportAPI, STATUS_COLORS, PRIORITY_COLORS, CATEGORIES, formatTimeAgo, calculateSLA } from '../services/supportService';
import { toast } from 'react-toastify';
import api from '../services/api';
import './ITSupport.css';

const ITSupport = () => {
  // State
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&h=400&fit=crop&crop=center&q=80');

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    client_code: '',
    assigned_to: '',
    priority: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Comment form
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch hero image from settings
  useEffect(() => {
    const fetchHeroImage = async () => {
      try {
        const settingsData = await api.get('/api/settings').then(res => res.data.grouped || {}).catch(() => ({}));
        const appearanceSettings = settingsData.appearance || [];
        const heroSetting = appearanceSettings.find(s => s.setting_key === 'it_support_hero_image_url');
        if (heroSetting?.setting_value) {
          setHeroImage(heroSetting.setting_value);
        }
      } catch (error) {
        console.error('Error fetching hero image:', error);
      }
    };
    fetchHeroImage();
  }, []);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...filters, page, limit: 20 };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await supportAPI.getTickets(params);
      setTickets(response.data.tickets);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  const fetchStats = async () => {
    try {
      const response = await supportAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await supportAPI.getTechnicians();
      setTechnicians(response.data.technicians);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await supportAPI.getClients();
      setClients(response.data.clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTechnicians();
    fetchClients();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const openTicketDetail = async (ticketId) => {
    try {
      setDetailLoading(true);
      setShowDetailModal(true);
      const response = await supportAPI.getTicket(ticketId);
      setSelectedTicket(response.data.ticket);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Failed to load ticket details');
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedTicket(null);
    setNewComment('');
    setIsInternal(false);
  };

  const updateTicket = async (field, value) => {
    if (!selectedTicket) return;
    try {
      const response = await supportAPI.updateTicket(selectedTicket.id, { [field]: value });
      setSelectedTicket({ ...selectedTicket, ...response.data.ticket });
      fetchTickets();
      fetchStats();
      toast.success('Ticket updated');
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTicket) return;

    try {
      setSubmitting(true);
      const response = await supportAPI.addComment(selectedTicket.id, {
        comment: newComment,
        is_internal: isInternal
      });

      setSelectedTicket({
        ...selectedTicket,
        comments: [...selectedTicket.comments, response.data.comment]
      });

      setNewComment('');
      setIsInternal(false);
      toast.success('Comment added');
      fetchTickets();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      client_code: '',
      assigned_to: '',
      priority: '',
      search: ''
    });
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Hero style with light blue gradient
  const heroStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(43, 127, 224, 0.45) 0%, rgba(30, 90, 160, 0.4) 50%, rgba(20, 60, 120, 0.45) 100%), url('${heroImage}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    borderRadius: '12px',
    padding: '50px 40px',
    minHeight: '220px',
    marginBottom: '24px',
    color: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  return (
    <div className="it-support-page">
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
          IT Support
        </h1>
        <p style={{ fontSize: 16, opacity: 0.95, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
          Manage support tickets from client portals
        </p>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="support-stats">
          <div className="stat-card" onClick={() => handleFilterChange('status', '')}>
            <span className="stat-number">{Object.values(stats.by_status).reduce((a, b) => a + b, 0)}</span>
            <span className="stat-label">Total Tickets</span>
          </div>
          <div className="stat-card new" onClick={() => handleFilterChange('status', 'new')}>
            <span className="stat-number">{stats.by_status.new || 0}</span>
            <span className="stat-label">New</span>
          </div>
          <div className="stat-card in-progress" onClick={() => handleFilterChange('status', 'in_progress')}>
            <span className="stat-number">{stats.by_status.in_progress || 0}</span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-card urgent" onClick={() => handleFilterChange('priority', 'urgent')}>
            <span className="stat-number">{stats.by_priority.urgent || 0}</span>
            <span className="stat-label">Urgent</span>
          </div>
          <div className="stat-card unassigned" onClick={() => handleFilterChange('assigned_to', 'unassigned')}>
            <span className="stat-number">{stats.unassigned_urgent_count || 0}</span>
            <span className="stat-label">Needs Assignment</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="support-filters">
        <div className="filter-row">
          <input
            type="text"
            placeholder="Search tickets..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_COLORS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select"
          >
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_COLORS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <select
            value={filters.client_code}
            onChange={(e) => handleFilterChange('client_code', e.target.value)}
            className="filter-select"
          >
            <option value="">All Clients</option>
            {clients.map(client => (
              <option key={client.client_code} value={client.client_code}>
                {client.client_name}
              </option>
            ))}
          </select>

          <select
            value={filters.assigned_to}
            onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
            className="filter-select"
          >
            <option value="">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {technicians.map(tech => (
              <option key={tech.id} value={tech.id}>{tech.name}</option>
            ))}
          </select>

          {(filters.status || filters.priority || filters.client_code || filters.assigned_to || filters.search) && (
            <button className="reset-btn" onClick={resetFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Tickets Table */}
      {loading ? (
        <div className="loading">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🎫</span>
          <h3>No tickets found</h3>
          <p>
            {Object.values(filters).some(v => v)
              ? 'Try adjusting your filters.'
              : 'Support tickets from client portals will appear here.'}
          </p>
        </div>
      ) : (
        <>
          <div className="tickets-table-container">
            <table className="tickets-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Client</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(ticket => {
                  const priorityStyle = PRIORITY_COLORS[ticket.priority];
                  const statusStyle = STATUS_COLORS[ticket.status];

                  return (
                    <tr
                      key={ticket.id}
                      onClick={() => openTicketDetail(ticket.id)}
                      className={ticket.status === 'new' ? 'new-ticket' : ''}
                    >
                      <td className="ticket-number">{ticket.ticket_number}</td>
                      <td>{ticket.client_name || ticket.client_code}</td>
                      <td className="ticket-subject">
                        <span className="subject-text">{ticket.subject}</span>
                        {ticket.comment_count > 0 && (
                          <span className="comment-count">{ticket.comment_count}</span>
                        )}
                      </td>
                      <td>{CATEGORIES[ticket.category] || ticket.category}</td>
                      <td>
                        <span
                          className="priority-badge"
                          style={{ background: priorityStyle.bg, color: priorityStyle.text }}
                        >
                          {priorityStyle.label}
                        </span>
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ background: statusStyle.bg, color: statusStyle.text }}
                        >
                          {statusStyle.label}
                        </span>
                      </td>
                      <td>{ticket.assigned_to_name || <em>Unassigned</em>}</td>
                      <td>{formatTimeAgo(ticket.created_at)}</td>
                      <td>{formatTimeAgo(ticket.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Ticket Detail Modal */}
      {showDetailModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content ticket-detail-modal" onClick={e => e.stopPropagation()}>
            {detailLoading ? (
              <div className="loading">Loading ticket details...</div>
            ) : selectedTicket ? (
              <>
                <div className="modal-header">
                  <div className="modal-title-section">
                    <h2>{selectedTicket.ticket_number}</h2>
                    <span className="client-badge">{selectedTicket.client_name}</span>
                  </div>
                  <button className="modal-close" onClick={closeModal}>&times;</button>
                </div>

                <div className="modal-body">
                  {/* Ticket Info */}
                  <div className="ticket-info-section">
                    <h3 className="ticket-subject-title">{selectedTicket.subject}</h3>

                    <div className="ticket-meta">
                      <div className="meta-item">
                        <label>Submitted By</label>
                        <span>{selectedTicket.submitted_by_name}</span>
                        {selectedTicket.submitted_by_email && (
                          <a href={`mailto:${selectedTicket.submitted_by_email}`}>
                            {selectedTicket.submitted_by_email}
                          </a>
                        )}
                        {selectedTicket.submitted_by_phone && (
                          <span>{selectedTicket.submitted_by_phone}</span>
                        )}
                      </div>

                      <div className="meta-item">
                        <label>Category</label>
                        <span>{CATEGORIES[selectedTicket.category] || selectedTicket.category}</span>
                      </div>

                      <div className="meta-item">
                        <label>Priority</label>
                        <select
                          value={selectedTicket.priority}
                          onChange={(e) => updateTicket('priority', e.target.value)}
                          className="inline-select"
                          style={{
                            background: PRIORITY_COLORS[selectedTicket.priority].bg,
                            color: PRIORITY_COLORS[selectedTicket.priority].text
                          }}
                        >
                          {Object.entries(PRIORITY_COLORS).map(([key, { label }]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="meta-item">
                        <label>Status</label>
                        <select
                          value={selectedTicket.status}
                          onChange={(e) => updateTicket('status', e.target.value)}
                          className="inline-select"
                          style={{
                            background: STATUS_COLORS[selectedTicket.status].bg,
                            color: STATUS_COLORS[selectedTicket.status].text
                          }}
                        >
                          {Object.entries(STATUS_COLORS).map(([key, { label }]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="meta-item">
                        <label>Assigned To</label>
                        <select
                          value={selectedTicket.assigned_to || ''}
                          onChange={(e) => updateTicket('assigned_to', e.target.value || null)}
                          className="inline-select"
                        >
                          <option value="">Unassigned</option>
                          {technicians.map(tech => (
                            <option key={tech.id} value={tech.id}>{tech.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="meta-item">
                        <label>Created</label>
                        <span>{formatDate(selectedTicket.created_at)}</span>
                      </div>

                      {selectedTicket.first_response_at && (
                        <div className="meta-item">
                          <label>First Response</label>
                          <span>{formatDate(selectedTicket.first_response_at)}</span>
                        </div>
                      )}

                      {selectedTicket.resolved_at && (
                        <div className="meta-item">
                          <label>Resolved</label>
                          <span>{formatDate(selectedTicket.resolved_at)}</span>
                        </div>
                      )}
                    </div>

                    {/* SLA Indicator */}
                    {!['resolved', 'closed'].includes(selectedTicket.status) && (
                      <div className={`sla-indicator ${calculateSLA(selectedTicket.created_at, selectedTicket.priority).status}`}>
                        <span className="sla-label">SLA:</span>
                        <span className="sla-time">
                          {calculateSLA(selectedTicket.created_at, selectedTicket.priority).label}
                        </span>
                      </div>
                    )}

                    {/* Description */}
                    <div className="ticket-description">
                      <label>Description</label>
                      <p>{selectedTicket.description}</p>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div className="comments-section">
                    <h4>Comments & Updates</h4>

                    {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                      <div className="comments-list">
                        {selectedTicket.comments.map(comment => (
                          <div
                            key={comment.id}
                            className={`comment ${comment.author_type} ${comment.is_internal ? 'internal' : ''}`}
                          >
                            <div className="comment-header">
                              <span className="comment-author">
                                {comment.author_name}
                                {comment.author_type === 'svt' && <span className="svt-badge">SVT</span>}
                                {comment.is_internal && <span className="internal-badge">Internal</span>}
                              </span>
                              <span className="comment-time">{formatTimeAgo(comment.created_at)}</span>
                            </div>
                            <div className="comment-body">{comment.comment}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-comments">No comments yet</p>
                    )}

                    {/* Add Comment Form */}
                    <form className="add-comment-form" onSubmit={addComment}>
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows={3}
                      />
                      <div className="comment-form-footer">
                        <label className="internal-checkbox">
                          <input
                            type="checkbox"
                            checked={isInternal}
                            onChange={(e) => setIsInternal(e.target.checked)}
                          />
                          <span>Internal note (not visible to client)</span>
                        </label>
                        <button type="submit" disabled={!newComment.trim() || submitting}>
                          {submitting ? 'Adding...' : 'Add Comment'}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Attachments */}
                  {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                    <div className="attachments-section">
                      <h4>Attachments</h4>
                      <div className="attachments-list">
                        {selectedTicket.attachments.map(attachment => (
                          <a
                            key={attachment.id}
                            href={attachment.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment-item"
                          >
                            <span className="attachment-icon">📎</span>
                            <span className="attachment-name">{attachment.file_name}</span>
                            {attachment.file_size && (
                              <span className="attachment-size">
                                ({Math.round(attachment.file_size / 1024)}KB)
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="error">Failed to load ticket details</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ITSupport;
